import type { UseFormReturn } from 'react-hook-form';

export type Option = {
  value: string;
  label: string;
  children?: {
    title: string;
    options: Option[];
  }[];
};

export type OptionWithChecked = Omit<Option, 'children'> & {
  checked: boolean;
  children?: { title: string; options: OptionWithChecked[] }[];
};

export type FlatSlice = Record<string, boolean | undefined>;

export type FormValues = {
  flat: Record<string, boolean>;
};

export function collectDescendantIds(option: Option): string[] {
  if (!option.children) {
    return [option.value];
  }
  return [option.value, ...option.children.flatMap((child) => child.options.flatMap(collectDescendantIds))];
}

export function shallowEqual(a: FlatSlice, b: FlatSlice): boolean {
  const keysA = Object.keys(a);
  if (keysA.length !== Object.keys(b).length) {
    return false;
  }
  return keysA.every((key) => {
    return a[key] === b[key];
  });
}

export function getSlice(option: Option, flat: FlatSlice): FlatSlice {
  const ids = collectDescendantIds(option);
  return Object.fromEntries(ids.map((id) => [id, flat[id]]));
}

export function getAllSlices(opts: Option[], flat: FlatSlice): Map<string, FlatSlice> {
  const map = new Map<string, FlatSlice>();
  function visit(option: Option) {
    map.set(option.value, getSlice(option, flat));
    option.children?.forEach((child) => child.options.forEach(visit));
  }
  opts.forEach(visit);
  return map;
}

export function collectAllValues(opts: Option[]): string[] {
  const values: string[] = [];
  for (const option of opts) {
    values.push(...collectDescendantIds(option));
  }
  return [...new Set(values)];
}

export function collectLeafIds(opts: Option[]): string[] {
  const values: string[] = [];
  for (const option of opts) {
    if (collectDescendantIds(option).length === 1) {
      values.push(option.value);
    } else {
      for (const child of option.children ?? []) {
        values.push(...collectLeafIds(child.options));
      }
    }
  }
  return values;
}

export function getNodeState(option: Option, flat: Record<string, boolean | undefined>) {
  if (!option.children?.length) {
    return flat[option.value] ?? false;
  }

  const childStates = option.children.flatMap((child) => child.options.map((n) => getNodeState(n, flat)));
  const allTrue = childStates.every(Boolean);
  const allFalse = childStates.every((v) => !v);

  if (allTrue) {
    return true;
  }
  if (allFalse) {
    return false;
  }
  return 'indeterminate' as const;
}

export function handleParentChange(
  form: UseFormReturn<FormValues>,
  option: Option,
  next: boolean | 'indeterminate',
): void {
  const isChecked = next === true;
  const ids = collectDescendantIds(option);
  ids.forEach((value) => {
    form.setValue(`flat.${value}`, isChecked, { shouldDirty: true, shouldTouch: true });
  });
}

export function handleLeafChange(
  form: UseFormReturn<FormValues>,
  value: string,
  next: boolean | 'indeterminate',
): void {
  const isChecked = next === true;
  form.setValue(`flat.${value}`, isChecked, { shouldDirty: true, shouldTouch: true });
}

export function buildNested(opts: Option[], flat: FormValues['flat']): OptionWithChecked[] {
  return opts.map((option) => ({
    ...option,
    checked: Boolean(flat[option.value]),
    children: option.children?.map((child) => ({
      ...child,
      options: buildNested(child.options, flat),
    })),
  }));
}
